import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

/* PILOTO-1D (firma #82) — LA PÍLDORA DEL REGRESO, una sola vez en todo el repo.
 *
 * QUÉ ES. El botón de «volver» que NO se pierde al recorrer una pantalla larga:
 * se queda pegado arriba, con el vidrio de la familia del bottom nav y 44 pt de
 * área de toque.
 *
 * DE DÓNDE SALE. Nació inline en Analytics (COS-15 + COS-15b) y esta tanda lo
 * SACÓ DE AHÍ TAL CUAL —mismas clases, mismo orden de nodos, mismos testids
 * configurables— para que el detalle de grupo lo REUSE en vez de estrenar una
 * copia. Una copia se desvía al primer retoque: el día que alguien suba el
 * contraste o cambie el radio en una pantalla, la otra se queda atrás y el
 * producto termina con dos píldoras que se parecen. Aquí hay una.
 *
 * ── POR QUÉ `sticky` Y NO `fixed`, y por qué NO escribe una safe area propia ──
 *
 * El scroll de la app NO es el de la ventana: es el `<main>` del chasis
 * (App.js), que ya arranca por debajo del header y por debajo del notch — su
 * `mt` vale el alto de la barra mas la zona segura de arriba (desde
 * dashboard-rediseno, de `lib/medidasDelChasis.js`). Un `sticky top-*` se pega al
 * borde de ESE scrollport, o sea ya dentro de la zona segura; sumarle aquí otro
 * `env(safe-area-inset-top)` contaría el notch DOS veces y dejaría la píldora
 * colgada con un hueco del alto de la muesca. Mismo criterio que OfflinePill
 * («no escribe ni una safe area propia, hereda las del header»).
 *
 * `w-fit` es lo que impide congelar la fila entera: la caja pegajosa mide lo que
 * mide el botón, así que el contenido se ve pasar por detrás y por los lados.
 * `h-11` = 44 pt de área de toque.
 *
 * LAS CLASES SE EXPORTAN porque hay un test que las compara contra las que
 * Analytics pinta de verdad: si algún día las dos superficies dejaran de salir
 * de aquí, ese test lo dice antes que un usuario.
 */

export const PILL_WRAPPER_CLASS = 'sticky top-2 z-10 w-fit';

// El texto va en `text-white` (COS-15b): el gris `#a0a0a0` se leía apagado sobre
// el vidrio, y esta píldora es la única salida de la pantalla. La afordancia la
// pone el vidrio y el borde, no el color del texto.
//
// ── T5.2 (firma #136): DE `/20` A `/80`, Y ES UN ARREGLO, NO UN RETOQUE ──────
//
// EL DEFECTO, reportado sobre la pantalla de edición del banco: el botón se ve
// «encimado al contenido» al recorrer la pantalla. La geometría es la correcta y
// está pensada así —`sticky` no saca del flujo y `w-fit` deja que el contenido
// pase por detrás y por los lados, tal como dice el docblock de arriba—, pero el
// fondo era `bg-[#0e0e0e]/20`: **un 20 % de opacidad**. Comparado con el resto
// del cromo flotante de la casa, la píldora era cuatro veces más transparente:
//
//     header (App.js)            bg-[#0e0e0e]/80
//     bottom nav (MobileBottomNav) bg-[#0e0e0e]/95
//     esta píldora, hasta T5.1    bg-[#0e0e0e]/20   ← el defecto
//
// Con cuatro quintas partes de transparencia, toda la legibilidad quedaba en
// manos del `backdrop-blur`, y un blur no borra: difumina. El texto blanco que
// pasa por detrás se sigue LEYENDO a través del botón, y eso es exactamente lo
// que se ve como «encimado».
//
// POR QUÉ SE NOTA EN EDICIÓN Y CASI NO EN LAS OTRAS: una card de pregunta con
// sus once cajas abiertas mide dos o tres pantallas de teléfono, así que ahí
// SIEMPRE hay contenido pasando por debajo. En una lista corta, la píldora
// apenas llega a solaparse con nada.
//
// Se alinea con el header (`/80`) y no con el bottom nav (`/95`) a propósito:
// `/80` conserva el efecto de vidrio que la píldora tiene desde COS-15 —se
// intuye que hay algo detrás— sin dejar que se lea.
//
// ── TANDA 11 (firma #177) — Y APRIETA ───────────────────────────────────────
//
// EL HUECO: esta píldora es la ÚNICA salida de su pantalla, y desde #149c no
// daba ninguna señal al dedo. Su única afordancia de «te oí» era el posado del
// botón de la casa, que en un teléfono dejó de existir; el destello nativo
// tampoco está (lo apaga el preflight del framework). O sea: cero.
//
// EL ESPEJO: la variante de presión repite el paso de fondo que el botón de la
// casa ya le sirve al ratón, servido al dedo. Cero colores nuevos — es el mismo
// tono del tema que usan los botes de basura del Panel para su apretón.
//
// EL TEXTO NO SE TOCA. El posado del botón de la casa también aclara el texto,
// y aquí eso NO se copia a propósito: el blanco de esta píldora es una decisión
// firmada (COS-15b) porque el gris se leía apagado sobre el vidrio, y esta es la
// única salida de la pantalla. Mismo criterio que los botes del Panel, que
// tampoco copiaron esa mitad.
//
// LA DURACIÓN CERO no es adorno: el botón de la casa arrastra su transición de
// color (150 ms) y un tap dura menos, así que sin anularla el resalte no
// llegaría a verse. Al soltar, la vuelta se anima con la transición de siempre.
//
// ALCANCE, DECLARADO: esta constante la comparten Analytics y las tres
// pantallas de grupos. El apretón les llega a todas, y es lo correcto — es el
// MISMO componente y el mismo gesto; una copia con clases propias para una sola
// pantalla es justo lo que el docblock de arriba existe para impedir.
export const PILL_BUTTON_CLASS =
  'h-11 rounded-full border border-[#262626] bg-[#0e0e0e]/80 backdrop-blur-2xl ' +
  'backdrop-saturate-150 shadow-[0_10px_30px_rgba(0,0,0,0.55)] text-white px-4 ' +
  'active:bg-accent active:duration-0';

export default function StickyBackPill({ onClick, label, testid, buttonTestid }) {
  return (
    <div className={PILL_WRAPPER_CLASS} data-testid={testid}>
      <Button variant="ghost" onClick={onClick} className={PILL_BUTTON_CLASS}
        data-testid={buttonTestid}>
        <ArrowLeft className="w-4 h-4 mr-1.5" /> {label}
      </Button>
    </div>
  );
}
